import { TabPanel, TabView } from 'primereact/tabview';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import ActionButtons from '@features/campaigns/details/components/ActionButtons/ActionButtons';
import CampaignDetails from '@features/campaigns/details/components/CampaignDetails/CampaignDetails';
import Performance from '@features/campaigns/performance/containers/Performance/Performance';
import { CombinedSequentialId } from '@features/campaigns/shared/components/CombinedSequentialId/CombinedSequentialId.tsx';
import { Spinner } from '@features/campaigns/shared/components/Spinner/Spinner';
import { areDisplayCreativesComplete } from '@features/campaigns/shared/config/adProductsCountryConfig.helpers.ts';
import { getCampaignAdProductsCountryConfig } from '@features/campaigns/shared/config/adProductsCountryConfig.ts';
import useCampaignDetails from '@features/campaigns/shared/hooks/useCampaignDetails';
import { CampaignCountryProvider } from '@features/campaigns/shared/providers/CampaignCountryProvider/CampaignCountryProvider.tsx';
import { useCampaignCountry } from '@features/campaigns/shared/providers/CampaignCountryProvider/useCampaignCountry.ts';
import { CampaignTypes, InvalidSectionsItem } from '@features/campaigns/shared/types/campaign.ts';
import { AccessRights } from '@shared/api/commonApiInterfaces.ts';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes, InfoPanelVariants } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { LinkText } from '@shared/components/LinkText/LinkText.tsx';
import placeholderCommonStyles from '@shared/components/PlaceholderCard/PlaceholderCard.module.scss';
import { PlaceholderCard } from '@shared/components/PlaceholderCard/PlaceholderCard.tsx';
import { i18nNameSpace } from '@shared/consts/i18n';
import { useSubmitButtonValidation } from '@shared/hooks/useSubmitButtonValidation.ts';
import { useUserOnboardingState } from '@shared/hooks/useUserOnboardingState.tsx';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { routePaths } from '@shared/router/routePaths';
import { DataStatuses } from '@shared/types/common.ts';
import { ONBOARDING_STATE, PossibleOnboardingState } from '@shared/types/userProfile.types.ts';

import { campaignDetailsDTO, CampaignDetailsViewData } from './CampaignDetailsPage.dto';
import styles from './CampaignDetailsPage.module.scss';

function determineInfoPanelMessage(onboardingState?: PossibleOnboardingState) {
  switch (onboardingState) {
    case ONBOARDING_STATE.BILLING_INFO_NOT_FILLED:
      return {
        message: 'builder.steps.step05.infoPanels.provideBillingInfo',
        type: InfoPanelTypes.WARNING,
      };
    case ONBOARDING_STATE.BILLING_INFO_IN_APPROVAL:
      return {
        message: 'builder.steps.step05.infoPanels.billingInfoInApproval',
        type: InfoPanelTypes.INFO,
      };
    case ONBOARDING_STATE.ADVERTISERS_NOT_ADDED:
      return {
        message: 'builder.steps.step05.infoPanels.advertisersNotAdded',
        type: InfoPanelTypes.WARNING,
      };
    case ONBOARDING_STATE.ADVERTISERS_IN_APPROVAL:
      return {
        message: 'builder.steps.step05.infoPanels.advertisersInApproval',
        type: InfoPanelTypes.INFO,
      };
    default:
      return {
        message: '',
        type: InfoPanelTypes.INFO,
      };
  }
}

interface CampaignDetailsPageContentProps {
  campaignDetailsData: CampaignDetailsViewData;
  invalidSections: InvalidSectionsItem[];
  isFetching: boolean;
  campaignId: string;
  onboardingState?: PossibleOnboardingState;
  isOnboardingCompleted: boolean;
  isCurrentUserExternal: boolean;
}

function CampaignDetailsPageContent({
  campaignDetailsData,
  invalidSections,
  isFetching,
  campaignId,
  onboardingState,
  isOnboardingCompleted,
  isCurrentUserExternal,
}: CampaignDetailsPageContentProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { pathname } = useLocation();
  const isPerformanceView = pathname.includes(routePaths.campaignPerformance.url(campaignId));
  const activeTabIndex = isPerformanceView ? '1' : '0';
  const [activeIndex, setActiveIndex] = useState(activeTabIndex);

  const { determineIfShouldDisableSubmit } = useSubmitButtonValidation();
  const { countryCode } = useCampaignCountry();
  const { isExternalAgency, isExternalDirectClient } = useUserDetails();

  const countryConfig = getCampaignAdProductsCountryConfig(countryCode);

  const setActiveTabHandler = (tabIndex: number) => {
    setActiveIndex(`${tabIndex}`);
  };

  const isCampaignSelfService = campaignDetailsData.type === CampaignTypes.SELF_SERVICE;

  const areDisplayCreativesUploaded = areDisplayCreativesComplete(countryConfig, {
    lShapeImageUrl: campaignDetailsData.adProducts.lShapeImageUrl,
    landingImageUrl: campaignDetailsData.adProducts.landingImageUrl,
    switchInLeftImageUrl: campaignDetailsData.adProducts.switchInLeftImageUrl,
    switchInBottomImageUrl: campaignDetailsData.adProducts.switchInBottomImageUrl,
  });
  const isVideoCreativeUploaded = Boolean(campaignDetailsData.adProducts.videoSpotUrl);
  const isVideoCreativeStatusApproved = Boolean(
    campaignDetailsData.adProducts.videoSpotStatus === DataStatuses.APPROVED,
  );
  const isInvalidSectionsEmpty = invalidSections.length === 0;
  const {
    isDisplayAdProductSelected,
    isVideoAdProductSelected,
    videoSpotUrl,
    lShapeImageUrl,
    landingImageUrl,
    switchInLeftImageUrl,
    switchInBottomImageUrl,
    videoSpotStatus,
  } = campaignDetailsData.adProducts;

  const shouldDisableSubmitButton = determineIfShouldDisableSubmit({
    isInvalidSectionsEmpty,
    isOnboardingCompleted,
    isExternalDirectClient,
    isExternalAgency,
    countryCode,
    campaignProps: {
      billingContactCode: campaignDetailsData.campaignDetails.billingContactCode,
      advertiserCode: campaignDetailsData.campaignDetails.advertiserCode,
      type: campaignDetailsData.type,
      videoSpotUrl,
      videoSpotStatus,
      lShapeImageUrl,
      landingImageUrl,
      switchInLeftImageUrl,
      switchInBottomImageUrl,
      isVideoAdProductSelected,
      isDisplayAdProductSelected,
    },
  });

  function determineIfShouldDisableApproveButton() {
    if (!isCampaignSelfService) {
      return false;
    }

    if (isVideoAdProductSelected && isDisplayAdProductSelected) {
      return !areDisplayCreativesUploaded || !isVideoCreativeUploaded || !isVideoCreativeStatusApproved;
    }

    if (isDisplayAdProductSelected) {
      return !areDisplayCreativesUploaded;
    }

    if (isVideoAdProductSelected) {
      return !isVideoCreativeUploaded || !isVideoCreativeStatusApproved;
    }

    return false;
  }

  const shouldDisplayPerformanceTab = campaignDetailsData.accessRights.includes(AccessRights.EXPORT_REPORT);

  const shouldDisableApproveButton = determineIfShouldDisableApproveButton();

  const shouldShowInfoPanel =
    onboardingState === ONBOARDING_STATE.BILLING_INFO_NOT_FILLED ||
    onboardingState === ONBOARDING_STATE.BILLING_INFO_IN_APPROVAL ||
    onboardingState === ONBOARDING_STATE.ADVERTISERS_NOT_ADDED ||
    onboardingState === ONBOARDING_STATE.ADVERTISERS_IN_APPROVAL;

  const infoPanelData = determineInfoPanelMessage(onboardingState);

  const advertiserBillingContactNotFilled =
    (onboardingState === ONBOARDING_STATE.COMPLETED &&
      !(
        campaignDetailsData.campaignDetails.advertiserName && campaignDetailsData.campaignDetails.billingContactName
      )) ||
    false;

  return (
    <div className={styles.wrapper}>
      {!isPerformanceView && isFetching && <Spinner variant='fullScreenOverlay' />}
      <div className={styles.contentWrapper}>
        <ActionButtons
          shouldSubmitButtonBeDisabled={shouldDisableSubmitButton}
          shouldApproveButtonBeDisabled={shouldDisableApproveButton}
          campaignName={campaignDetailsData.campaignDetails.campaignName}
          campaignAccessRights={campaignDetailsData.accessRights}
        />
        <div className={styles.campaignDetailsWrapper}>
          <h2 className={styles.campaignName}>{campaignDetailsData.campaignDetails.campaignName}</h2>
          <p className={styles.campaignSubname}>
            <CombinedSequentialId
              sequentialId={campaignDetailsData.sequentialId}
              billingSequentialId={campaignDetailsData.billingSequentialId}
              type={campaignDetailsData.type}
            />
          </p>
        </div>
        <TabView activeIndex={Number(activeIndex)} onTabChange={(e) => setActiveTabHandler(e.index)}>
          <TabPanel header={t('page.campaignDetails.viewTab.campaignDetails')}>
            {campaignId && <Navigate to={routePaths.campaignDetails.url(campaignId)} />}

            {shouldShowInfoPanel && (
              <InfoPanel type={infoPanelData.type} variant={InfoPanelVariants.COMPACT}>
                <p>
                  <Trans
                    t={t}
                    i18nKey={infoPanelData.message}
                    components={[
                      <LinkText
                        externalLink={false}
                        key={'infoPanels.step05InfoPanel'}
                        target='_self'
                        to={routePaths.userAdminPanel.routePath}
                      />,
                    ]}
                  />
                </p>
              </InfoPanel>
            )}

            {advertiserBillingContactNotFilled && (
              <InfoPanel type={InfoPanelTypes.WARNING} variant={InfoPanelVariants.COMPACT}>
                <p>
                  <Trans
                    t={t}
                    i18nKey={'builder.steps.step05.infoPanels.advertisersReadyNotAdded'}
                    components={[
                      <LinkText
                        externalLink={false}
                        key={'infoPanels.advertisersReadyNotAdded'}
                        target='_self'
                        to={routePaths.campaignBuilderStep01.url(campaignId)}
                      />,
                    ]}
                  />
                </p>
              </InfoPanel>
            )}

            <CampaignDetails
              data={campaignDetailsData}
              invalidSections={invalidSections}
              onboardingState={onboardingState}
              isCurrentUserExternal={isCurrentUserExternal}
              isOnboardingCompleted={isOnboardingCompleted}
            />
          </TabPanel>
          {shouldDisplayPerformanceTab && (
            <TabPanel header={t('page.campaignDetails.viewTab.performance')}>
              {campaignId && <Navigate to={routePaths.campaignPerformance.url(campaignId)} />}
              <Performance
                campaignEndDate={campaignDetailsData.endDate}
                campaignStartDate={campaignDetailsData.startDate}
                campaignFormat={campaignDetailsData.formatType}
                campaignName={campaignDetailsData.campaignDetails.campaignName}
                campaignStatus={campaignDetailsData.campaignStatus}
                campaignType={campaignDetailsData.type}
              />
            </TabPanel>
          )}
        </TabView>
      </div>
    </div>
  );
}

function CampaignDetailsPage() {
  const { campaignId } = useParams();

  const { isFetching, data } = useCampaignDetails({ campaignId: campaignId! });

  const { isExternalAgency, isExternalDirectClient } = useUserDetails();
  const isCurrentUserExternal = isExternalDirectClient || isExternalAgency;

  const { onboardingState, isOnboardingCompleted } = useUserOnboardingState({
    campaignType: data?.type,
    campaignCreatedBy: data?.createdBy,
    accessRights: data?.accessRights,
  });

  if (!data) {
    return (
      <div className={placeholderCommonStyles.placeholderCardWrapper}>
        <PlaceholderCard textKey='placeholderLoading' />
      </div>
    );
  }

  const campaignDetailsData: CampaignDetailsViewData = campaignDetailsDTO(data);

  return (
    <CampaignCountryProvider countryCode={campaignDetailsData.country}>
      <CampaignDetailsPageContent
        campaignDetailsData={campaignDetailsData}
        invalidSections={data.invalidSections}
        isFetching={isFetching}
        campaignId={campaignId!}
        onboardingState={onboardingState}
        isOnboardingCompleted={isOnboardingCompleted}
        isCurrentUserExternal={isCurrentUserExternal}
      />
    </CampaignCountryProvider>
  );
}

export default CampaignDetailsPage;
